import { memo } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { ChartFrame } from '@/components/charts/ChartFrame';
import { formatCompact } from '@/lib/format';
import { TIER_COLORS } from '@/lib/tierColors';

type CostsRevenuesChartProps = {
  data: { month: number; mrr: number; cost: number; total: number }[];
  operationalBreakEvenMonth: number | null;
  // Affiche la courbe « Revenu total » (MRR + marge d'entrée) : utile quand des frais d'entrée existent.
  showTotal?: boolean;
};

const TOOLTIP_STYLE = {
  backgroundColor: 'var(--card)',
  border: '1px solid var(--border)',
  color: 'var(--card-foreground)',
  fontSize: 12,
};

export const CostsRevenuesChart = memo(function CostsRevenuesChart({
  data,
  operationalBreakEvenMonth,
  showTotal = false,
}: CostsRevenuesChartProps) {
  return (
    <ChartFrame>
      <LineChart data={data} margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
        <XAxis
          dataKey="month"
          tickFormatter={(v: number) => `M${v}`}
          tick={{ fill: 'currentColor', fontSize: 11 }}
        />
        <YAxis
          tickFormatter={formatCompact}
          tick={{ fill: 'currentColor', fontSize: 11 }}
          width={56}
        />
        <Tooltip
          formatter={(value, name) =>
            typeof value === 'number' ? [formatCompact(value), String(name)] : value
          }
          contentStyle={TOOLTIP_STYLE}
          labelFormatter={(label) => `Mois ${String(label)}`}
        />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        {operationalBreakEvenMonth !== null && (
          <ReferenceLine
            x={operationalBreakEvenMonth}
            stroke="#888"
            strokeDasharray="4 2"
            label={{ value: 'Break-even', position: 'insideTopRight', fontSize: 11, fill: '#888' }}
          />
        )}
        <Line
          type="monotone"
          dataKey="mrr"
          name="MRR"
          stroke={TIER_COLORS[2]}
          dot={false}
          strokeWidth={2}
          isAnimationActive={false}
        />
        <Line
          type="monotone"
          dataKey="cost"
          name="Coûts"
          stroke={TIER_COLORS[5]}
          dot={false}
          strokeWidth={2}
          isAnimationActive={false}
        />
        {showTotal && (
          <Line
            type="monotone"
            dataKey="total"
            name="Revenu total (avec entrée)"
            stroke={TIER_COLORS[1]}
            dot={false}
            strokeWidth={2}
            strokeDasharray="5 3"
            isAnimationActive={false}
          />
        )}
      </LineChart>
    </ChartFrame>
  );
});
